import { Modal, Table, Switch, Typography, message, Alert } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { t } from 'ttag';

import SearchFilter, { SearchFilterIcon } from '@components/SearchFilter';

import { useFeatureFlagsQuery, useUpdateFeatureFlagMutation } from './queries';

const { Text } = Typography;

const FeatureFlagsModal = ({ onClose, event }) => {
  const { data: featureFlags = [], isFetching, isError } = useFeatureFlagsQuery(event.shortcode);

  const updateMutation = useUpdateFeatureFlagMutation();

  const handleStatusChange = async (flag, enabled) => {
    await updateMutation.mutateAsync(
      {
        shortcode: event.shortcode,
        flagId: flag.id,
        enabled,
      },
      {
        onSuccess: () => {
          message.success(t`Feature flag "${flag.id}" updated successfully`);
        },
        onError: () => {
          message.error(t`Failed to update feature flag "${flag.id}". Please try again.`);
        },
      },
    );
  };

  const columns = [
    {
      title: t`Feature Flag`,
      dataIndex: 'id',
      key: 'id',
      filterDropdown: SearchFilter,
      filterIcon: SearchFilterIcon,
      onFilter: (value, record) => record.id.toString().toLowerCase().includes(value.toLowerCase()),
      render: id => <Text code>{id}</Text>,
    },
    {
      title: t`Status`,
      dataIndex: 'enabled',
      key: 'status',
      width: '100px',
      render: (enabled, flag) => (
        <Switch
          checked={enabled}
          loading={updateMutation.variables?.flagId === flag.id && updateMutation.isLoading}
          onChange={checked => handleStatusChange(flag, checked)}
          size="small"
        />
      ),
    },
  ];

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      title={t`Feature Flags for ${event.name}`}
      open
      onCancel={handleClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      {isError && <Alert message={t`Failed to load feature flags.`} type="error" showIcon />}
      {!isError && (
        <Table
          columns={columns}
          dataSource={featureFlags}
          rowKey="id"
          pagination={false}
          loading={isFetching}
          size="small"
        />
      )}
    </Modal>
  );
};

FeatureFlagsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shortcode: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
};

export default FeatureFlagsModal;
